import { t, Trans } from '@lingui/macro';
import IntersectionObserver from '@researchgate/react-intersection-observer';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';

import ActivityFeed from 'Components/ActivityFeed/ActivityFeed.async';
import LatestRequests from 'Components/LatestRequests/LatestRequests';
import ProvidersSlider from 'Components/ProvidersSlider/ProvidersSlider';
import ServiceSearch from 'Components/ServiceSearch/ServiceSearch';
import config from 'config/config';
import { Heading1, Heading3 } from 'uikit/Heading';
import routes from 'utils/routeTranslator';

import { AllCategoriesCity$Props, combineProviders, combineRequests, getAverageRating } from '../AllCategories.helpers';
import AllCategoriesNav from '../AllCategoriesNav/AllCategoriesNav';
import AllCategoriesSection from '../AllCategoriesSection/AllCategoriesSection';
import { removeVINQuestionFromRequests } from 'utils/VIN/removeVINQuestionFromRequests';
import './AllCategoriesCity.scss';

const AllCategoriesCity = (props: AllCategoriesCity$Props) => {
  const { allCategories, city, providersCounts } = props;
  const params = useParams<{ city?: string }>();
  const { nameLocativeCase, name, lat, long, region } = city;
  const ratingValue = getAverageRating(allCategories).toFixed(1);

  const thisYear = new Date().getFullYear();
  const providersCount = providersCounts.registered < 10 ? 10 : providersCounts.registered;
  const siteTitle = t`Fixly ${city.name} | ${providersCount} wykonawców | Najlepsi w ${thisYear}`;
  const siteDescription = t`Fixly.pl - ${providersCount} wykonawców ${
    nameLocativeCase || name
  } - sprawnie, wygodnie, lokalnie! Dodaj zapytanie i szybko nawiąż kontakt.`;

  const providers = combineProviders(allCategories, 5);
  const requests = combineRequests(allCategories, 5);

  const handleIntersectionChange = (event: IntersectionObserverEntry) => {
    window.postMessage(
      {
        type: 'toggle_header_search',
        isHidden: event.isIntersecting,
      },
      window.location.origin
    );
  };

  return (
    <div className="allCategoriesCity">
      <Helmet>
        <title>{siteTitle}</title>
        <meta property="og:description" content={siteDescription} />
        <meta name="msapplication-tooltip" content={siteDescription} />
        <meta name="description" content={siteDescription} />
        <link rel="canonical" href={config.SITE_URL + routes.get('categories_seo', { city: params.city || '' })} />
      </Helmet>

      <div className="allCategoriesCity__header">
        <div className="allCategoriesCity__info">
          <Heading1 className="allCategoriesCity__title">
            <Trans>Fixly {name}: znajdź usługę, której szukasz</Trans>
          </Heading1>
          <p className="allCategoriesCity__description">
            <Trans>
              Mamy <b>{providersCounts.registered}</b> wykonawców, ocenianych średnio na{' '}
              <span className="allCategoriesCity__rating">{ratingValue}</span>,<br />
              <b>{providersCounts.online}</b> jest teraz online
            </Trans>
          </p>
          <p className="allCategoriesCity__description allCategoriesCity__description_mobile">
            <Trans>
              Mamy <b>{providersCounts.registered}</b> wykonawców, ocenianych średnio na{' '}
              <span className="allCategoriesCity__rating">{ratingValue}</span>, <b>{providersCounts.online}</b> jest
              teraz online
            </Trans>
          </p>
          <IntersectionObserver onChange={handleIntersectionChange}>
            <div className="allCategoriesCity__search">
              <ServiceSearch />
            </div>
          </IntersectionObserver>
        </div>
        <div className="allCategoriesCity__activityFeed">
          {/* @ts-expect-error type mismatch */}
          <ActivityFeed desktopShortVersion />
        </div>
      </div>
      <div className="allCategoriesCity__navigation">
        <AllCategoriesNav topCategories={allCategories} />
      </div>

      <div className="allCategoriesCity__categories">
        {allCategories.map((topCategory) => (
          <AllCategoriesSection {...topCategory} key={topCategory.slug} citySlug={city.slug} onClick={() => {}} />
        ))}
      </div>

      {providers.length > 0 && (
        <div className="allCategoriesCity__providers">
          <Heading3 className="allCategoriesCity__subtitle">
            <Trans>Najlepsi wykonawcy {nameLocativeCase || name}</Trans>
          </Heading3>
          <ProvidersSlider
            /* @ts-expect-error type mismatch */
            providers={providers}
            cityName={name}
            cityLat={lat}
            cityLong={long}
            regionName={region.name}
          />
        </div>
      )}

      {requests.length > 0 && (
        <div className="allCategoriesCity__requests">
          <Heading3 className="allCategoriesCity__subtitle">
            <Trans>Ostanie zapytania {nameLocativeCase || name}</Trans>
          </Heading3>
          {/* @ts-expect-error type mismatch */}
          <LatestRequests requests={removeVINQuestionFromRequests(requests)} />
        </div>
      )}
    </div>
  );
};

export default AllCategoriesCity;
